import { loadStripe } from '@stripe/stripe-js';
import { db } from '../firebase/config';
import {
   collection,
   doc,
   addDoc,
   updateDoc,
   serverTimestamp,
   getDoc,
   getDocs,
   query,
   where,
} from 'firebase/firestore';
import { TicketTier } from '../types/Event';
import axios from 'axios';

// Initialize Stripe with your publishable key
// Replace with your actual key from environment variables

export interface PaymentIntent {
   id: string;
   clientSecret: string;
   amount: number;
   currency: string;
   status: string;
   created: number;
}

/**
 * Create a payment intent for ticket purchase
 *
 * @param eventId - The ID of the event
 * @param tierId - The ID of the ticket tier being purchased
 * @param quantity - Number of tickets being purchased
 * @param userId - The ID of the user making the purchase
 * @returns The payment intent details
 */
export const createPaymentIntent = async (
   eventId: string,
   tierId: string,
   quantity: number,
   userId: string
): Promise<PaymentIntent> => {
   try {
      // Get the ticket tier to determine price

      const tierDoc = await getDoc(doc(db, 'ticketTiers', tierId));
      if (!tierDoc.exists()) {
         throw new Error('Ticket tier not found');
      }

      const tierData = tierDoc.data() as TicketTier;
      const totalAmount = tierData.price * quantity;

      // If the ticket is free, no need for payment intent
      if (totalAmount === 0) {
         // Handle free tickets separately
         return {
            id: 'free-ticket',
            clientSecret: '',
            amount: 0,
            currency: tierData.currency,
            status: 'succeeded',
            created: Date.now(),
         };
      }

      // Create a payment intent in Firestore first
      // This will trigger a Cloud Function to create a real Stripe payment intent
      const paymentIntentRef = await addDoc(collection(db, 'paymentIntents'), {
         eventId,
         tierId,
         userId,
         quantity,
         amount: totalAmount,
         currency: tierData.currency,
         status: 'created',
         createdAt: serverTimestamp(),
      });

      // Poll for the payment intent to be updated by the Cloud Function
      let attempts = 0;
      const maxAttempts = 10;

      while (attempts < maxAttempts) {
         attempts++;
         await new Promise((resolve) => setTimeout(resolve, 500));

         const updatedIntent = await getDoc(paymentIntentRef);
         const data = updatedIntent.data();

         if (data && data.clientSecret) {
            return {
               id: updatedIntent.id,
               clientSecret: data.clientSecret,
               amount: data.amount,
               currency: data.currency,
               status: data.status,
               created: data.createdAt ? data.createdAt.toMillis() : Date.now(),
            };
         }
      }

      throw new Error('Timeout waiting for payment intent creation');
   } catch (error) {
      console.error('Error creating payment intent:', error);
      throw error;
   }
};

/**
 * Process a successful ticket purchase after payment
 *
 * @param paymentIntentId - The ID of the payment intent
 * @param eventId - The ID of the event
 * @param tierId - The ID of the ticket tier
 * @param quantity - Number of tickets purchased
 * @param userId - The ID of the user who made the purchase
 */
export const finalizeTicketPurchase = async (
   paymentIntentId: string,
   eventId: string,
   tierId: string,
   quantity: number,
   userId: string
): Promise<string[]> => {
   try {
      // Get the ticket tier to update sold count
      const tierDoc = await getDoc(doc(db, 'ticketTiers', tierId));
      if (!tierDoc.exists()) {
         throw new Error('Ticket tier not found');
      }

      const tierData = tierDoc.data() as TicketTier;

      // Create an order record
      const orderRef = await addDoc(collection(db, 'orders'), {
         paymentIntentId,
         eventId,
         tierId,
         userId,
         quantity,
         status: 'completed',
         createdAt: serverTimestamp(),
      });

      // Create ticket records for each ticket
      const ticketIds: string[] = [];
      const ticketPromises = [];

      for (let i = 0; i < quantity; i++) {
         const ticketRef = await addDoc(collection(db, 'tickets'), {
            eventId,
            tierId,
            userId,
            orderId: orderRef.id,
            status: 'confirmed',
            purchaseDate: serverTimestamp(),
            checkedIn: null,
            cancelledAt: null,
            refundedAt: null,
         });

         ticketIds.push(ticketRef.id);

         // Update the ticket tier's sold count
         const tierRef = doc(db, 'ticketTiers', tierId);
         ticketPromises.push(
            updateDoc(tierRef, {
               sold: (tierData.sold || 0) + 1,
               updatedAt: serverTimestamp(),
            })
         );
      }

      await Promise.all(ticketPromises);

      return ticketIds;
   } catch (error) {
      console.error('Error finalizing ticket purchase:', error);
      throw error;
   }
};

/**
 * Get Stripe checkout for event ticket purchase
 *
 * @param eventId - The ID of the event
 * @param tierId - The ID of the ticket tier
 * @param quantity - Number of tickets to purchase
 */
export const initiateStripeCheckout = async (
   eventId: string,
   tierId: string,
   quantity: number = 1
) => {
   try {
      if (!process.env.REACT_APP_STRIPE_PUBLIC_KEY) {
         return;
      }

      const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

      // Get active user ID
      const userId = localStorage.getItem('userId') || '';
      if (!userId) {
         throw new Error('User not authenticated');
      }

      // Create payment intent
      const paymentIntent = await createPaymentIntent(eventId, tierId, quantity, userId);

      // If it's a free ticket, finalize purchase immediately
      if (paymentIntent.id === 'free-ticket') {
         return await finalizeTicketPurchase('free-ticket', eventId, tierId, quantity, userId);
      }

      // Load Stripe
      const stripe = await stripePromise;
      if (!stripe) {
         throw new Error('Failed to load Stripe');
      }

      // Redirect to checkout
      const { error } = await stripe.redirectToCheckout({
         sessionId: paymentIntent.id,
         successUrl: `${window.location.origin}/events/${eventId}/tickets/confirmation?intent=${paymentIntent.id}`,
         cancelUrl: `${window.location.origin}/events/${eventId}`,
      });

      if (error) {
         throw new Error(error.message);
      }

      return [];
   } catch (error) {
      console.error('Error initiating checkout:', error);
      throw error;
   }
};

/**
 * Get ticket purchase details
 *
 * @param paymentIntentId - The ID of the payment intent
 */
export const getTicketPurchaseDetails = async (paymentIntentId: string) => {
   try {
      // Get the payment intent
      const intentDoc = await getDoc(doc(db, 'paymentIntents', paymentIntentId));
      if (!intentDoc.exists()) {
         throw new Error('Payment intent not found');
      }

      const intentData = intentDoc.data();

      // Get the event
      const eventDoc = await getDoc(doc(db, 'events', intentData.eventId));
      if (!eventDoc.exists()) {
         throw new Error('Event not found');
      }

      // Get the ticket tier
      const tierDoc = await getDoc(doc(db, 'ticketTiers', intentData.tierId));
      if (!tierDoc.exists()) {
         throw new Error('Ticket tier not found');
      }

      // Get any tickets created for this payment
      const tickets = await getDocs(
         query(collection(db, 'tickets'), where('paymentIntentId', '==', paymentIntentId))
      );

      return {
         paymentIntent: {
            id: intentDoc.id,
            ...intentData,
         },
         event: {
            id: eventDoc.id,
            ...eventDoc.data(),
         },
         ticketTier: {
            id: tierDoc.id,
            ...tierDoc.data(),
         },
         tickets: tickets.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
         })),
      };
   } catch (error) {
      console.error('Error getting ticket purchase details:', error);
      throw error;
   }
};

/**
 * Connect a venue to Stripe Connect
 *
 * @param businessName - The name of the venue business
 * @param email - The email address of the account
 * @returns The Stripe account ID and status
 */
export const connectToStripe = async (
   businessName: string,
   email: string
): Promise<{ accountId: string; status: string }> => {
   try {
      // This is a mock implementation for development
      // In a production environment, this would initiate a Stripe Connect OAuth flow
      // or create a Stripe Connect account via the API

      // For demonstration purposes, we'll simulate a successful connection
      // with a fake account ID
      const mockAccountId = 'acct_' + Math.random().toString(36).substring(2, 15);

      // Create a record in the stripeAccounts collection
      const accountData = {
         businessName,
         email,
         accountId: mockAccountId,
         status: 'active',
         connected: true,
         createdAt: serverTimestamp(),
         updatedAt: serverTimestamp(),
         payoutsEnabled: true,
         chargesEnabled: true,
         detailsSubmitted: true,
      };

      await addDoc(collection(db, 'stripeAccounts'), accountData);

      return {
         accountId: mockAccountId,
         status: 'active',
      };
   } catch (error) {
      console.error('Error connecting to Stripe:', error);
      throw error;
   }
};

/**
 * Create a payment intent for package booking
 *
 * @param venueId - The ID of the venue
 * @param packageId - The ID of the package being booked
 * @param bookingDetails - The booking details (date, time, etc.)
 * @param userId - The ID of the user making the booking
 * @returns The payment intent details
 */
export const createPackageBookingPaymentIntent = async (
   venueId: string,
   packageId: string,
   bookingDetails: {
      date: string;
      timeSlot: string;
      guests: number;
      specialRequests?: string;
   },
   userId: string
): Promise<PaymentIntent> => {
   try {
      // Get the package to determine price
      const packageDoc = await getDoc(doc(db, 'venues', venueId, 'packages', packageId));

      if (!packageDoc.exists()) {
         throw new Error('Package not found');
      }

      const packageData = packageDoc.data();

      // Calculate the total amount
      const basePrice = packageData.discountedPrice || packageData.price;
      const totalAmount = basePrice * bookingDetails.guests;

      // Get the venue's Stripe account ID
      const venueDoc = await getDoc(doc(db, 'venues', venueId));

      if (!venueDoc.exists()) {
         throw new Error('Venue not found');
      }

      const venueData = venueDoc.data();
      const stripeAccountId = venueData.stripeAccountId;

      // Create a payment intent
      // In a production environment, this would call your backend API
      // to create a real payment intent with Stripe
      const paymentIntent = {
         id: 'pi_' + Math.random().toString(36).substring(2, 15),
         clientSecret:
            'pi_' +
            Math.random().toString(36).substring(2, 15) +
            '_secret_' +
            Math.random().toString(36).substring(2, 15),
         amount: totalAmount,
         currency: 'usd',
         status: 'requires_payment_method',
         created: Date.now(),
      };

      // Store the payment intent in Firestore
      await addDoc(collection(db, 'packagePaymentIntents'), {
         id: paymentIntent.id,
         clientSecret: paymentIntent.clientSecret,
         venueId,
         packageId,
         packageName: packageData.name,
         userId,
         amount: totalAmount,
         currency: 'usd',
         status: paymentIntent.status,
         bookingDetails,
         stripeAccountId,
         createdAt: serverTimestamp(),
         updatedAt: serverTimestamp(),
      });

      return paymentIntent;
   } catch (error) {
      console.error('Error creating package booking payment intent:', error);
      throw error;
   }
};

/**
 * Finalize a package booking after successful payment
 *
 * @param paymentIntentId - The ID of the payment intent
 * @returns The booking ID
 */
export const finalizePackageBooking = async (paymentIntentId: string): Promise<string> => {
   try {
      // Get the payment intent details
      const paymentsQuery = query(
         collection(db, 'packagePaymentIntents'),
         where('id', '==', paymentIntentId)
      );

      const querySnapshot = await getDocs(paymentsQuery);

      if (querySnapshot.empty) {
         throw new Error('Payment intent not found');
      }

      const paymentDoc = querySnapshot.docs[0];
      const paymentData = paymentDoc.data();

      // Update the payment intent status
      await updateDoc(paymentDoc.ref, {
         status: 'succeeded',
         updatedAt: serverTimestamp(),
      });

      // Create a new booking
      const bookingData = {
         packageId: paymentData.packageId,
         packageName: paymentData.packageName,
         venueId: paymentData.venueId,
         venueName: paymentData.venueName,
         userId: paymentData.userId,
         bookingDate: paymentData.bookingDetails.date,
         timeSlot: paymentData.bookingDetails.timeSlot,
         guests: paymentData.bookingDetails.guests,
         specialRequests: paymentData.bookingDetails.specialRequests || '',
         amount: paymentData.amount,
         currency: paymentData.currency,
         status: 'confirmed',
         paymentId: paymentIntentId,
         redemptionCode: generateRedemptionCode(),
         createdAt: serverTimestamp(),
         updatedAt: serverTimestamp(),
      };

      const bookingRef = await addDoc(collection(db, 'packageBookings'), bookingData);

      return bookingRef.id;
   } catch (error) {
      console.error('Error finalizing package booking:', error);
      throw error;
   }
};

/**
 * Get all payouts for a venue
 *
 * @param venueId - The ID of the venue
 * @returns A list of payouts
 */
export const getVenuePayouts = async (venueId: string): Promise<any[]> => {
   try {
      // In a production environment, this would call the Stripe API
      // to get real payout data

      // For demonstration purposes, we'll return mock data
      return [
         {
            id: 'po_' + Math.random().toString(36).substring(2, 15),
            amount: 10000, // $100.00
            currency: 'usd',
            status: 'paid',
            createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 1 week ago
            arrivalDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
         },
         {
            id: 'po_' + Math.random().toString(36).substring(2, 15),
            amount: 25000, // $250.00
            currency: 'usd',
            status: 'paid',
            createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000), // 2 weeks ago
            arrivalDate: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000), // 12 days ago
         },
         {
            id: 'po_' + Math.random().toString(36).substring(2, 15),
            amount: 15000, // $150.00
            currency: 'usd',
            status: 'in_transit',
            createdAt: new Date(),
            arrivalDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000), // 2 days from now
         },
      ];
   } catch (error) {
      console.error('Error getting venue payouts:', error);
      throw error;
   }
};

/**
 * Generate a unique redemption code for package bookings
 *
 * @returns A unique redemption code
 */
const generateRedemptionCode = (): string => {
   const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Excluding similar looking characters
   let code = '';

   // Generate a 8-character code
   for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
   }

   // Add hyphens for readability (e.g., ABCD-1234)
   return `${code.slice(0, 4)}-${code.slice(4, 8)}`;
};

/**
 * Get the Stripe Connect account status for a venue
 *
 * @param venueId - The ID of the venue
 * @returns The account status
 */
export const getStripeConnectStatus = async (venueId: string): Promise<any> => {
   try {
      // Get the venue document
      const venueDoc = await getDoc(doc(db, 'venues', venueId));

      if (!venueDoc.exists()) {
         throw new Error('Venue not found');
      }

      const venueData = venueDoc.data();

      if (!venueData.stripeAccountId) {
         return {
            connected: false,
            status: 'not_connected',
         };
      }

      // In a production environment, this would call the Stripe API
      // to get the real account status

      // For demonstration purposes, we'll return mock data
      return {
         connected: true,
         status: 'active',
         accountId: venueData.stripeAccountId,
         payoutsEnabled: true,
         chargesEnabled: true,
         detailsSubmitted: true,
      };
   } catch (error) {
      console.error('Error getting Stripe Connect status:', error);
      throw error;
   }
};

/**
 * Refresh subscription status after payment completion
 * This function can be called after successful payment to ensure the frontend
 * has the latest subscription status from Stripe
 *
 * @param userEmail - The email of the user whose subscription should be refreshed
 * @returns Promise<any> - The updated subscription data
 */
export const refreshSubscriptionStatus = async (userEmail: string): Promise<any> => {
   try {
      console.log('🔄 Refreshing subscription status for:', userEmail);

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4001';
      const response = await axios.get(
         `${apiUrl}/api/subscriptions/status?email=${encodeURIComponent(userEmail)}&forceRefresh=true`
      );

      if (response.data.success) {
         console.log('✅ Subscription status refreshed successfully:', response.data);
         return response.data;
      } else {
         throw new Error(response.data.error || 'Failed to refresh subscription status');
      }
   } catch (error: any) {
      console.error('❌ Error refreshing subscription status:', error);
      throw new Error(
         error.response?.data?.error || error.message || 'Failed to refresh subscription status'
      );
   }
};

/**
 * Utility function to be called after successful payment completion
 * This ensures the user gets immediate access after payment
 *
 * @param userEmail - The email of the user who completed payment
 * @param onSuccess - Callback function to execute on successful refresh
 * @param onError - Callback function to execute on error
 */
export const handlePostPaymentSubscriptionRefresh = async (
   userEmail: string,
   onSuccess?: (data: any) => void,
   onError?: (error: Error) => void
): Promise<void> => {
   try {
      console.log('🎯 Handling post-payment subscription refresh for:', userEmail);

      // Wait a moment for webhook processing
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const subscriptionData = await refreshSubscriptionStatus(userEmail);

      if (onSuccess) {
         onSuccess(subscriptionData);
      }

      console.log('✅ Post-payment subscription refresh completed successfully');
   } catch (error: any) {
      console.error('❌ Post-payment subscription refresh failed:', error);

      if (onError) {
         onError(error);
      }

      // Don't throw here - we don't want payment success to appear as failure
      // Just log the error and let the user continue
   }
};